import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { errorCode } from '@/api/errors';
import type { components } from '@/api/qraft/schema';
import { AlertBox } from '@/common/AlertBox';
import { LargeArrowBackButton } from '@/common/LargeArrowBackButton';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import { CheckboxLabel, WBSCheckbox } from '@/common/WBSCheckbox';
import { useCoordinatorAccess } from '~/hooks/useIsCoordinator';
import { useLanguageLabels } from '~/hooks/useLanguageLabels';
import { FieldError, FieldTitle } from '~/styles/forms/fieldFeedback';
import { FormField, StyledInput } from '~/styles/forms/formElements';
import { SelectGroup } from '~/styles/forms/styledSelectGroup';
import { FormContainer, GroupHeaderWrapper } from '~/styles/layoutElements';
import type { LanguageCode } from '~/types/congregationGroupTypes';
import { localeToApiLanguage } from '~/utils/languageFormat';

type StudentType = components['schemas']['StudentType'];

/**
 * The server requires at least this many characters and sets no upper bound.
 * Below it the create call refuses anyway, so the availability lookup doesn't
 * fire.
 */
const MIN_SUBDOMAIN_LENGTH = 3;

/** Long enough that a lookup is not fired per keystroke, short enough to feel live. */
const SUBDOMAIN_CHECK_DEBOUNCE_MS = 400;

/**
 * The five refusals this form renders under a field, typed as catalogue keys so
 * a typo is a compile error.
 */
type FieldErrorKey =
  | 'wbs.duplicate-subdomain'
  | 'wbs.invalid-subdomain'
  | 'wbs.bad-word-in-subdomain'
  | 'wbs.invalid-url'
  | 'wbs.bad-word-in-custom-url';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/campaign/add',
)({
  component: AddCampaignScreen,
});

/**
 * Legacy's Add Campaign form, on the same gate as the table behind it.
 *
 * The server's refusals each land on the field they're about. The two bad-word
 * codes stay separate, since each names its own field and has its own
 * translated string.
 */
function AddCampaignScreen(): ReactElement | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groupName } = Route.useParams();
  const { isCoordinator, isResolved } = useCoordinatorAccess();
  const refused = isResolved && !isCoordinator;

  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = groupData?.id;

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState(false);
  const [domainRequiredError, setDomainRequiredError] = useState(false);
  // `en`, not empty: legacy's select is required with no empty option, so the
  // value shown selected is the one that posts.
  const [language, setLanguage] = useState<LanguageCode>('en');
  const languageLabels = useLanguageLabels();
  const [studentType, setStudentType] = useState<StudentType>('ALL');
  const [useCustomDomain, setUseCustomDomain] = useState(false);
  // Two fields, not one: legacy clears whichever input it hides, so a typed
  // subdomain can't be resubmitted as a custom URL by ticking a box.
  const [subdomain, setSubdomain] = useState('');
  const [customUrl, setCustomUrl] = useState('');

  const {
    mutateAsync: createCampaign,
    isPending,
    error: createError,
    reset: resetCreateError,
  } = api.createGroupCampaign.useMutation();

  // A live availability lookup. Legacy's front-end form had none, so a
  // duplicate showed only on submit.
  const [checkedSubdomain, setCheckedSubdomain] = useState('');
  useEffect(() => {
    const candidate = subdomain.trim();
    if (useCustomDomain || candidate.length < MIN_SUBDOMAIN_LENGTH) {
      setCheckedSubdomain('');
      return;
    }
    const timer = setTimeout(
      () => setCheckedSubdomain(candidate),
      SUBDOMAIN_CHECK_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [subdomain, useCustomDomain]);

  const { data: subdomainCheck } = api.checkCampaignSubdomain.useQuery(
    { query: { subdomain: checkedSubdomain } },
    { enabled: checkedSubdomain !== '' },
  );
  const subdomainTaken = subdomainCheck?.available === false;

  const handleBack = (): void => {
    navigate({
      to: '/groups/$groupName/campaign/',
      params: { groupName },
    });
  };

  const code = errorCode(createError);

  /** The refusal that belongs under the subdomain input, if this one does. */
  const subdomainErrorKey = ((): FieldErrorKey | null => {
    if (subdomainTaken && !useCustomDomain) return 'wbs.duplicate-subdomain';
    if (useCustomDomain) return null;
    if (code === 'duplicate_subdomain') return 'wbs.duplicate-subdomain';
    if (code === 'invalid_subdomain') return 'wbs.invalid-subdomain';
    if (code === 'campaign_subdomain_bad_word')
      return 'wbs.bad-word-in-subdomain';
    return null;
  })();

  /** The refusal that belongs under the custom URL input, if this one does. */
  const customUrlErrorKey = ((): FieldErrorKey | null => {
    if (!useCustomDomain) return null;
    if (code === 'invalid_url') return 'wbs.invalid-url';
    if (code === 'campaign_custom_url_bad_word')
      return 'wbs.bad-word-in-custom-url';
    return null;
  })();

  /**
   * Anything the two field slots don't claim. `campaign_target_required` can't
   * happen from this form, since the checkbox makes the fields exclusive and
   * the empty case is caught below, but it's shown rather than swallowed.
   */
  const showGenericError =
    code !== undefined &&
    subdomainErrorKey === null &&
    customUrlErrorKey === null;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (groupId === undefined) return;
    resetCreateError();

    const trimmedName = name.trim();
    const target = (useCustomDomain ? customUrl : subdomain).trim();
    setNameError(trimmedName === '');
    setDomainRequiredError(target === '');
    if (trimmedName === '' || target === '') return;

    try {
      await createCampaign({
        path: { group_id: groupId },
        body: {
          campaign_name: trimmedName,
          language: localeToApiLanguage(language),
          student_type: studentType,
          // Exactly one of the pair is sent. The server refuses both or
          // neither, rather than reproducing legacy's bug of validating one
          // field and storing the other.
          subdomain: useCustomDomain ? null : target,
          custom_url: useCustomDomain ? target : null,
        },
      });
      await api.listGroupCampaigns.invalidateQueries();
      navigate({
        to: '/groups/$groupName/campaign/',
        params: { groupName },
        search: { created: true },
      });
    } catch {
      // Rendered from `createError` above, field by field.
    }
  };

  useEffect(() => {
    if (refused) {
      navigate({
        to: '/groups/$groupName/',
        params: { groupName },
        replace: true,
      });
    }
  }, [refused, groupName, navigate]);

  if (refused) return null;

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('sidebar.campaigns')}
          placement="group_campaigns"
        />
      </GroupHeaderWrapper>
      {showGenericError && (
        <AlertBox variant="error">
          {t('wbs.something-went-wrong-please-2')}
        </AlertBox>
      )}
      <FormContainer>
        <LargeArrowBackButton onClick={handleBack} />
        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
        >
          <FormField>
            <FieldTitle htmlFor="name" $hasError={nameError}>
              {t('wbs.name')}
            </FieldTitle>
            <StyledInput
              id="name"
              type="text"
              style={{ width: '100%' }}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (e.target.value.trim()) setNameError(false);
              }}
            />
            {nameError && (
              <FieldError>{t('wbs.this-field-is-required')}</FieldError>
            )}
          </FormField>

          <FormField>
            <FieldTitle htmlFor="language">{t('wbs.languages')}</FieldTitle>
            <SelectGroup>
              <select
                id="language"
                value={language}
                onChange={(e) => setLanguage(e.target.value as LanguageCode)}
                style={{ color: 'var(--grey-900)' }}
              >
                <option value="en">{languageLabels.en}</option>
                <option value="es">{languageLabels.es}</option>
                <option value="fr">{languageLabels.fr}</option>
                <option value="pt-br">{languageLabels['pt-br']}</option>
              </select>
              <span className="icon">
                <FontAwesomeIcon icon={faCaretDown} />
              </span>
            </SelectGroup>
          </FormField>

          <FormField>
            <FieldTitle htmlFor="studentType">{t('wbs.user-type')}</FieldTitle>
            <SelectGroup>
              <select
                id="studentType"
                value={studentType}
                style={{ color: 'var(--grey-900)' }}
                onChange={(e) => {
                  const value = e.target.value;
                  if (
                    value === 'ALL' ||
                    value === 'POSTAL' ||
                    value === 'ONLINE'
                  ) {
                    setStudentType(value);
                  }
                }}
              >
                <option value="ALL">{t('lessons.all-students')}</option>
                <option value="POSTAL">{t('wbs.postal-students-only')}</option>
                <option value="ONLINE">{t('wbs.online-students-only')}</option>
              </select>
              <span className="icon">
                <FontAwesomeIcon icon={faCaretDown} />
              </span>
            </SelectGroup>
          </FormField>

          <FormField>
            <CheckboxLabel htmlFor="useCustomDomain">
              <WBSCheckbox
                id="useCustomDomain"
                checked={useCustomDomain}
                onChange={(e) => {
                  setUseCustomDomain(e.target.checked);
                  // Legacy clears both inputs on every toggle.
                  setSubdomain('');
                  setCustomUrl('');
                  setDomainRequiredError(false);
                  resetCreateError();
                }}
              />
              {t('wbs.use-custom-domain')}
            </CheckboxLabel>
          </FormField>

          <FormField>
            <FieldTitle
              htmlFor="domainInput"
              $hasError={
                domainRequiredError ||
                subdomainErrorKey !== null ||
                customUrlErrorKey !== null
              }
            >
              {useCustomDomain ? t('wbs.custom-url') : t('wbs.subdomain')}
            </FieldTitle>
            {useCustomDomain ? (
              <StyledInput
                id="domainInput"
                type="text"
                value={customUrl}
                onChange={(e) => {
                  setCustomUrl(e.target.value);
                  if (e.target.value.trim()) setDomainRequiredError(false);
                }}
                style={{ width: '100%' }}
              />
            ) : (
              // No domain suffix beside the input: legacy showed one from its
              // language-to-host map, which v3 keeps server-side, so a static
              // suffix would name the wrong host for most languages and
              // environments. The created campaign's address shows in the table
              // this form returns to.
              <StyledInput
                id="domainInput"
                type="text"
                value={subdomain}
                onChange={(e) => {
                  setSubdomain(e.target.value);
                  if (e.target.value.trim()) setDomainRequiredError(false);
                }}
                style={{ width: '250px' }}
              />
            )}
            {domainRequiredError && (
              <FieldError>{t('wbs.this-field-is-required')}</FieldError>
            )}
            {!domainRequiredError && subdomainErrorKey && (
              <FieldError>{t(subdomainErrorKey)}</FieldError>
            )}
            {!domainRequiredError && customUrlErrorKey && (
              <FieldError>{t(customUrlErrorKey)}</FieldError>
            )}
          </FormField>

          <WBSButton wide type="submit" isLoading={isPending}>
            {t('actions.submit')}
          </WBSButton>
        </form>
      </FormContainer>
    </>
  );
}
